import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { selectOffice } from '~/actions';
import { useAppSelector } from '~/hooks/useAppSelector';
import type { TViewedOfficeScope } from '~/types/ViewedOfficeScope';

/**
 * Before a Payment History or Invoices row opens its details, selects an office in the
 * header that can read that row (GOTU-19472). The detail pages read and act as the header
 * office, and with a sibling location selected there the row's shift is refused (403), so
 * a location's page selects that location and the organization's page selects the
 * signed-in account. Nothing changes while the signed-in account is selected: it reads
 * every location it can open. The current URL is kept, so the row's own navigation still
 * pushes and Back returns to the list.
 *
 * The row navigates in the same click, before the switch answers. That is safe because `App`
 * shows its spinner in place of every route while `SELECT_OFFICE` loads (`useInitialLoading`),
 * so the details page mounts only after `SELECT_OFFICE_SUCCESS` has reset `jobReducer`, and
 * reads its job then.
 */
export const useSelectScopeOffice = (scope: TViewedOfficeScope) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);
  const signedInOfficeId = useAppSelector((state) => state.user.user?.id);

  return () => {
    if (!selectedOfficeId || selectedOfficeId === signedInOfficeId) return;

    let officeId: string | undefined;
    if (scope.kind === 'location') officeId = scope.officeId;
    else if (scope.kind === 'organization') officeId = signedInOfficeId;
    if (!officeId || officeId === selectedOfficeId) return;

    dispatch(selectOffice({ officeId, redirectUrl: `${pathname}${search}` }));
  };
};
